import { Link } from "react-router-dom";
import { Award, Linkedin, Sparkles, Target, TrendingUp, Users } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { FOUNDERS, MISSION, DPIIT, stats } from "@/data/mock";

const values = [
  { icon: Sparkles, title: "Uncompromising Quality", desc: "Every garment is inspected before pickup, hand-checked during processing, and photographed before delivery." },
  { icon: Target, title: "Speed You Can Set Your Watch By", desc: "24-hour turnaround is a promise, not a marketing line. Late orders are refunded." },
  { icon: Users, title: "Transparent Pricing", desc: "The price you see is the price you pay. No mystery weight charges. No hidden extras." },
  { icon: TrendingUp, title: "Built to Scale", desc: "We're building the infrastructure to bring quick-commerce laundry to 100+ Indian cities." },
];

export default function AboutPage() {
  return (
    <PageLayout>
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-16 bg-gradient-to-br from-sky-50/60 via-white to-lime-50/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            About Blitzwash
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-[1.1]">
            {MISSION.headline}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            {MISSION.body}
          </p>

          <div className="inline-flex items-center gap-3 mt-8 px-4 py-3 rounded-2xl bg-white border border-orange-200 shadow-sm">
            <Award className="w-6 h-6 text-orange-500" />
            <div className="text-left">
              <p className="text-sm font-bold text-slate-900">{DPIIT.label}</p>
              <p className="text-xs text-slate-500">Certificate #{DPIIT.number} · {DPIIT.fullLabel}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl sm:text-4xl font-extrabold text-slate-900">{s.value}</p>
              <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wider mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founders */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Founders</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Built by finance & operations veterans</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {FOUNDERS.map((f, i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-200 p-6 hover:border-sky-300 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white text-xl font-extrabold flex-shrink-0">
                    {f.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-sky-600 font-semibold uppercase tracking-wider">{f.role}</p>
                    <h3 className="text-xl font-bold text-slate-900 mt-1">{f.name}</h3>
                    <p className="text-sm text-slate-600 mt-2">{f.creds}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{f.previously}</p>
                    <a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-sky-600 hover:text-sky-700">
                      <Linkedin className="w-4 h-4" /> View LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-20 bg-slate-50/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Our Values</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">What we care about</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5 text-sky-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-sky-600 to-sky-800 text-white text-center">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-4">Ready to try Blitzwash?</h2>
          <p className="text-sky-100 mb-6">Free pickup, 24-hour turnaround, and 20% off your first order with code BLITZ20.</p>
          <Link to="/#contact" className="inline-block px-6 py-3 rounded-2xl bg-lime-400 hover:bg-lime-300 text-slate-900 font-bold text-sm transition-colors shadow-lg">
            Schedule Free Pickup
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
